import { useSyncExternalStore } from 'react';

/**
 * Mode "Samarkan identitas" viewer: menyembunyikan nama dan No. RM pasien di
 * overlay gambar dan header, serta mematikan "Sertakan data pasien" pada
 * Capture. Dipakai untuk gambar edukasi, presentasi, atau layar yang dilihat
 * orang lain.
 *
 * Disimpan di sessionStorage (per tab), BUKAN localStorage: dokter yang lupa
 * mematikannya tidak akan membaca pasien berikutnya tanpa identitas, karena
 * mode ini hilang saat tab ditutup. Header menampilkan penanda selama aktif.
 */
const STORAGE_KEY = 'lumivix_viewer_hide_identity';
/** Kelas di <html>; CSS-nya (App.css) menyembunyikan elemen `.overlay-identity`. */
export const IDENTITY_HIDDEN_CLASS = 'lumivix-identity-hidden';
const CHANGE_EVENT = 'lumivix:identity-privacy-changed';

export const isIdentityHidden = (): boolean => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/** Menyamakan kelas di <html> dengan nilai tersimpan (dipanggil saat viewer dibuka). */
export const applyIdentityPrivacyClass = (): void => {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.classList.toggle(IDENTITY_HIDDEN_CLASS, isIdentityHidden());
};

/** Melepas kelas saat meninggalkan viewer agar halaman lain tidak terpengaruh. */
export const clearIdentityPrivacyClass = (): void => {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.remove(IDENTITY_HIDDEN_CLASS);
  }
};

export const setIdentityHidden = (hidden: boolean): void => {
  try {
    if (hidden) {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage diblokir: mode tetap berlaku lewat kelas di <html> sampai halaman dimuat ulang.
  }
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle(IDENTITY_HIDDEN_CLASS, hidden);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const toggleIdentityHidden = (): boolean => {
  const next = !isIdentityHidden();
  setIdentityHidden(next);
  return next;
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
};

/** Hook React: komponen dirender ulang saat mode berubah. */
export const useIdentityHidden = (): boolean =>
  useSyncExternalStore(subscribe, isIdentityHidden, () => false);
